import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import { ParsedUrlQuery } from 'querystring'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  SanityProductCatalogueQuery,
  SanityPrintProductPage,
  SanityProductFragment,
} from '@data/sanity/queries/types/product'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPreview } from '@lib/sanity/client'
import {
  getPrintProductPage,
  getProductCatalogue,
  printProductTypes,
} from '@lib/sanity/product'
import { useViewContentFacebookEvent } from '@lib/facebook'
import { Locale } from '@lib/language'
import { useVariantMetadata } from '@lib/metadata'
import { useSetPrintShop } from '@lib/print/shop'
import { useProductWithInventory } from '@lib/product'
import { ProductVariantSelectionContextProvider } from '@lib/product-variant-selection-context'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext } from '@lib/routes'
import { useProductSchema } from '@lib/schema'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface ShopProductPageContentProps {
  product: SanityProductFragment
  locale: Locale
  page: SanityPrintProductPage
  site: SanitySiteFragment
  reviews?: Reviews
}

interface ShopProductPageProps {
  locale: Locale
  page: SanityPrintProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  reviews?: Reviews
}

type StaticShopProductPageParams = ParsedUrlQuery & {
  slug: string
  id: string
}

const ShopProductPageContent = ({
  product,
  locale,
  page,
  site,
  reviews,
}: ShopProductPageContentProps) => {
  useSetPrintShop(page.printShop)

  // Get product schema
  const productSchema = useProductSchema(site, product)

  // Trigger Facebook event
  useViewContentFacebookEvent(
    locale,
    product.title,
    site.settings.facebookEvents,
  )

  // Set product variant metadata
  useVariantMetadata()

  const modules = printProductTypes.includes(page.product.type)
    ? page.privateModules
    : page.modules

  if (!modules) {
    return null
  }

  return (
    <Layout page={page} site={site} productSchema={productSchema}>
      <Modules
        modules={modules}
        product={product}
        reviews={reviews}
        printShop={page.printShop}
      />
    </Layout>
  )
}

/**
 * The product page.
 */
const ShopProductPage = ({
  locale,
  page,
  site,
  reviews,
}: ShopProductPageProps) => {
  const product = useProductWithInventory(page, locale)

  return (
    <ProductVariantSelectionContextProvider product={product}>
      <ShopProductPageContent
        product={product}
        locale={locale}
        page={page}
        site={site}
        reviews={reviews}
      />
    </ProductVariantSelectionContextProvider>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticShopProductPageParams>): Promise<
  GetStaticPropsResult<ShopProductPageProps>
> {
  const printShopSlug = params?.id ?? ''
  const productSlug = params?.slug ?? ''
  const productPage = await getPrintProductPage(
    locale,
    printShopSlug,
    productSlug,
    getPreview(preview, previewData),
  )

  if (!productPage.page || !productPage.page.printShop) {
    return {
      notFound: true,
    }
  }

  const productCatalogue = await getProductCatalogue(locale)

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(locale)
  const reviews = await getReviews(
    productPage.page.modules ?? [],
    shopifyDomain,
    productPage.site.settings.stampedApiKey,
    productPage.page.product.id,
  )

  return {
    props: {
      locale,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticShopProductPageParams>
> {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default ShopProductPage
